import { BOOKING_STATE } from "./booking-states";
import { ARRIVAL_STATE } from "./arrival-states";
import { BOOKING_TYPE_TAG } from "./booking-type-tags";
import { BookingCustomer } from "./booking-customer";
import { VenueTable, VenueSection, VenueSetup } from "../venue-details/index.js";
import * as _ from "lodash";
import moment from "moment";
import { BookingNote } from "./booking-note";
import { BookingChange } from "./booking-change";
import { Event } from "../events/event";

export class Booking {
  constructor() {
    this.id = undefined;
    this.bookingId = { id: this.id};
    this.bookingState = BOOKING_STATE.Reserved;
    this.arrivalState = ARRIVAL_STATE.NotArrived;
    this.type;
    this.created;
    this.deleted;
    this.venueDetailsId;
    this.venueSetupId;
    this.venueSectionId;
    this.venueTableId;
    this.guestCount;
    this.arrival;
    this.departure;
    this.bookingCustomerId;
    this.customerMessage;
    this.bookingMessages = [];
    this.orderLines = [];
    this.tags = [];
    this.caseworker;
    this.notifyCaseworker = false;
    this.paymentReceived = false;
    this.notes = [];
    this.changeLog = [];
    this.eventId;
  }

  isValid() {
    let valid = !!this.venueDetailsId && !!this.venueSetupId && !!this.venueSectionId && !!this.guestCount && !!this.arrival;
    if((this.bookingState !== BOOKING_STATE.Requested && this.bookingState !== BOOKING_STATE.Reserved)
      ) {
      valid = valid && !!this.venueTableId
    }
    return valid;
  }

  addThemeTag(tag) {
    //overwrite existing THEME tag or push new tag.
    const themeTagInBookingIndex = _.findIndex(
      this.tags,
      (t) => t.indexOf("THEME:") >= 0
    );
    if (themeTagInBookingIndex >= 0) {
      this.tags.splice(themeTagInBookingIndex, 1, tag);
    } else {
      this.tags.push(tag);
    }
  }
  getThemeTag() {
    if (this.tags && this.tags.length > 0) {
      let themeTags = this.tags.filter(
        (t) => !!t && t.indexOf("THEME:") >= 0
      );
      if (themeTags.length > 0) return themeTags[0];
    }
    return undefined;
  }
  deleteThemeTag(tag){
    if (this.tags && this.tags.length > 0) {
      let index = _.findIndex(this.tags, t => t === tag);
      if (index >= 0) return this.tags.splice(index, 1);
    }
  }

  isWalkIn() {
    const walkInTagIndex = _.findIndex(this.tags, t => t === BOOKING_TYPE_TAG.WalkIn);
    return walkInTagIndex >= 0;
  }
  setAsWalkIn() {
    if(!this.isWalkIn()) this.tags.push(BOOKING_TYPE_TAG.WalkIn);
  }

  isRequest() {
    return this.bookingState === BOOKING_STATE.Requested;
  }

  isOvertime(){
    const overtimeFrom = moment(this.arrival).minutes(5);
    const now = moment();
    return now > overtimeFrom;
  }

  isAwaitingConfirmation(){
    if(this.tags) {
      let index = _.findIndex(this.tags, t => t === BOOKING_TYPE_TAG.AwaitsConfirmation);
      return index >= 0;
    }
    return false;
  }

  isEnded(){
    if(this.tags) {
      let index = _.findIndex(this.tags, t => t === BOOKING_TYPE_TAG.Ended);
      return index >= 0;
    }
    return false;
  }

  isForEvent() {
    return !!this.eventId && !!this.eventId.id;
  }

  static fromJSON(json) {
    if(!json) return undefined;

    const booking =  Object.assign(new Booking(), json);
    if(booking.bookingMessages) {
      booking.bookingMessages = booking.bookingMessages.map(m => {
        return BookingMessage.fromJSON(m);
      });
    }
    if(booking.orderLines) {
      booking.orderLines = booking.orderLines.map(m => {
        return OrderLine.fromJSON(m);
      });
    }
    if(booking.notes) {
      booking.notes = booking.notes.map(n => {
        return BookingNote.fromJSON(n);
      })
    }
    if(booking.changeLog) {
      booking.changeLog = booking.changeLog.map(l => {
        return BookingChange.fromJSON(l);
      })
    }
    return booking;
  }
}

export class BookingMessage {
  constructor(message, author) {
    this.message = message;
    this.author = author;
  }

  static fromJSON(json) {
    if(!json) return undefined;

    return Object.assign(new BookingMessage(), json);
  }
}

export class OrderLine {
  constructor() {
    this.name;
    this.quantity;
    this.unitPrice;
    this.description;
    this.category;
  }

  static fromJSON(json) {
    if(!json) return undefined;

    return Object.assign(new OrderLine(), json);
  }
}

export class EnhancedBooking {
  constructor(booking, bookingCustomer, reservedTable, reservedSection, reservedSetup, event) {
    this.booking = booking;
    this.bookingCustomer = bookingCustomer;
    this.reservedTable = reservedTable;
    this.reservedSection = reservedSection;
    this.reservedSetup = reservedSetup;
    this.reservedTableParts = [];
    this.minimumSpendAmount = undefined;
    this.event = event;
  }

  static fromJSON(json) {
    if(!json) return undefined;

    let booking = json.booking ? Booking.fromJSON(json.booking): undefined;
    let bookingCustomer = json.bookingCustomer ? BookingCustomer.fromJSON(json.bookingCustomer) : undefined;
    let reservedTable = json.reservedTable ? VenueTable.fromJSON(json.reservedTable) : undefined;
    let reservedSection = json.reservedSection ? VenueSection.fromJSON(json.reservedSection) : undefined;
    let reservedSetup = json.reservedSetup ? VenueSetup.fromJSON(json.reservedSetup) : undefined;
    let event = json.event ? Event.fromJSON(json.event) : undefined;

    const enhancedBooking = new EnhancedBooking(booking, bookingCustomer, reservedTable, reservedSection, reservedSetup, event);

    if(json.reservedTableParts)
      enhancedBooking.reservedTableParts = json.reservedTableParts.map(t => VenueTable.fromJSON(t));
    if(json.minimumSpendAmount)
      enhancedBooking.minimumSpendAmount = json.minimumSpendAmount;
    return enhancedBooking;
  }
}
